import React, { useState, useEffect, useCallback } from 'react';
import { logsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { Search, RefreshCw, RotateCcw, Trash2 } from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';

// Recycle Bin — deleted customers only, backed by the same ChangeLog "delete" entries
// and restore endpoint the Change Ledger uses (customer deletes store a full snapshot
// and are restorable — see routes/logs.js ENTITY_REGISTRY). This page is just a
// customer-focused, friendlier lens on that same data.

const fmtDate = (iso) => new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const normDigits = (s) => String(s || '').replace(/\D/g, '');

export default function RecycleBinPage() {
  useDarkReady();
  const { isAdmin } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [restoring, setRestoring] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await logsAPI.getAll({ entity: 'customer', action: 'delete', limit: 2000 });
      setLogs(data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load recycle bin');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const matches = (l) => {
    if (!search) return true;
    const q = search.toLowerCase(), qd = normDigits(search);
    return (l.customerName || '').toLowerCase().includes(q)
      || (l.username || '').toLowerCase().includes(q)
      || (qd.length > 0 && normDigits(l.mobile).includes(qd));
  };
  const filtered = logs.filter(matches);
  const active = filtered.filter(l => !l.restored);
  const restored = filtered.filter(l => l.restored);

  const handleRestore = async (l) => {
    if (!window.confirm(`Restore customer "${l.customerName || l.username}"? This recreates their full record.`)) return;
    setRestoring(l._id);
    try {
      const { data } = await logsAPI.restore(l._id);
      toast.success(data.message || 'Customer restored');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Restore failed');
    } finally {
      setRestoring(null);
    }
  };

  const Row = ({ l }) => (
    <div style={{ display: 'flex', gap: 12, padding: '12px 18px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
      <div style={{ width: 3, alignSelf: 'stretch', borderRadius: 2, background: l.restored ? 'var(--cb-0e9e96)' : 'var(--cb-db4c5c)', flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <b style={{ fontSize: '.9rem' }}>{l.customerName || '—'}</b>
          {l.username && <span className="font-mono" style={{ fontSize: '.78rem', color: 'var(--primary)' }}>{l.username}</span>}
          {l.mobile && <span style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>{l.mobile}</span>}
          {l.restored && <span style={{ fontSize: '.68rem', color: 'var(--c-0e9e96)', fontWeight: 700 }}>✓ restored</span>}
        </div>
        <div style={{ fontSize: '.76rem', color: 'var(--text-muted)', marginTop: 3 }}>
          Deleted {fmtDate(l.createdAt)}{l.changedByName ? ` by ${l.changedByName}` : ''}
        </div>
      </div>
      {isAdmin && !l.restored && (
        <button className="btn btn-secondary btn-sm" style={{ flexShrink: 0 }}
          disabled={restoring === l._id}
          onClick={() => handleRestore(l)}>
          <RotateCcw size={12} /> {restoring === l._id ? 'Restoring…' : 'Restore'}
        </button>
      )}
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Trash2 size={22} /> Recycle Bin
          </h1>
          <p className="page-subtitle">Deleted customers land here — restore any of them back into Customers, full record intact.</p>
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: 20 }}>
        <div className="stat-card">
          <div className="stat-info">
            <h3 style={{ fontFamily: 'monospace' }}>{active.length}</h3>
            <p style={{ textTransform: 'uppercase', letterSpacing: '.08em', fontSize: '.7rem' }}>In Bin</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <h3 style={{ fontFamily: 'monospace' }}>{restored.length}</h3>
            <p style={{ textTransform: 'uppercase', letterSpacing: '.08em', fontSize: '.7rem' }}>Restored</p>
          </div>
        </div>
      </div>

      <div className="filter-bar" style={{ marginBottom: 16 }}>
        <div className="search-input-wrapper" style={{ flex: 1, minWidth: 220 }}>
          <Search size={14} className="search-icon" />
          <input className="search-input" placeholder="Search by name, username or mobile number…"
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <button className="btn btn-secondary btn-sm" onClick={load}><RefreshCw size={13} /> Refresh</button>
      </div>

      <div className="card"><div className="card-body" style={{ padding: 0 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 48 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        ) : active.length === 0 && restored.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)' }}>
            <Trash2 size={36} style={{ opacity: .25, marginBottom: 10 }} />
            <div style={{ fontWeight: 700 }}>{logs.length === 0 ? 'Recycle bin is empty' : 'No matches'}</div>
            <p style={{ fontSize: '.83rem' }}>
              {logs.length === 0 ? 'Deleted customers will show up here automatically.' : 'Try a different name, username, or mobile number.'}
            </p>
          </div>
        ) : (
          <div>
            {active.map(l => <Row key={l._id} l={l} />)}
            {restored.map(l => <Row key={l._id} l={l} />)}
          </div>
        )}
      </div></div>
    </div>
  );
}
